import { setDoc, doc, getDoc, getDocs, query, where, collection, serverTimestamp, deleteDoc } from "firebase/firestore";
import { db } from "../config/FirebaseConfig"; // db exported from your config

class InvitationApi {
    // '<' operator is used to filter based on less than
    // '<=' operator is used to filter based on less than or equal to
    // '==' operator is used to filter based on equal to
    // '>' operator is used to filter based on greater than
    // '>=' operator is used to filter based on greater than or equal to
    // '!=' operator is used to filter based on not equal to
    // 'array-contains' operator is used to filter based on array contains
    // 'array-contains-any' operator is used to filter based on array contains any
    // 'in' operator is used to filter based on in
    // 'not-in' operator is used to filter based on not in

    /*
    // firestore collection: invitations/{inviteId}
    {
    email: "invitee@example.com",
    invitedBy: "user123",      // UID of inviter
    role: "producer" | "consumer",
    projectId: "abc123",       // optional
    token: "randomString123",  // for verification
    createdAt: Timestamp,
    accepted: false
    }*/

    async createInvitation(body = {}) {
        const token = Math.random().toString(36).substring(2, 15);
        if (!body || !body.email || !body.invitedBy || !body.role) {
            console.error("Invalid invitation data:", body);
            return { success: false, error: "Invalid invitation data" };
        }
        try {
            await setDoc(doc(db, "invitations", token),
                {
                    email: body.email,
                    invitedById: body.invitedById,
                    invitedBy: body.invitedBy || null, // Assuming invitedBy is a user object
                    companyId: body.companyId || null,
                    company: body.company || null, // Assuming company is a user object
                    role: body.role,
                    projectId: body.projectId || null,
                    token: token,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                    accepted: false,
                    isDeleted: false,
                }
            );
            return { success: true, token: token };
        } catch (error) {
            console.error("Error creating invitation:", error);
            return { success: false, error: error.message };
        }
    }

    async updateInvitation(id = null, body = {}) {
        if (!id || !body) {
            console.error("Invalid invitation data:", { id, body });
            return { success: false, error: "Invalid invitation data" };
        }
        try {
            await setDoc(doc(db, "invitations", id), {
                ...body,
                updatedAt: serverTimestamp(),
            }, { merge: true });

            return { success: true };
        } catch (error) {
            console.error("Error updating invitation:", error);
            return { success: false, error: error.message };
        }
    }

    async getInvitationByToken(token) {
        if (!token) {
            console.error("Invalid token:", token);
            return { success: false, error: "Invalid token" };
        }
        try {
            const docRef = doc(db, "invitations", token);
            const snapshot = await getDoc(docRef);
            if (!snapshot.exists()) {
                return { success: false, error: "Invitation not found" };
            }
            return { success: true, data: { id: snapshot.id, ...snapshot.data() } };
        } catch (error) {
            console.error("Error fetching invitation:", error);
            return { success: false, error: error.message };
        }
    }

    async getInvitationByProjectId(projectId) {
        if (!projectId) {
            console.error("Invalid projectId:", projectId);
            return { success: false, error: "Invalid projectId" };
        }
        try {
            const invitationsRef = collection(db, "invitations");
            // where projectId is equal to the provided projectId and accepted is false
            const querySnapshot = await getDocs(query(invitationsRef, where("projectId", "==", projectId), where("accepted", "==", false), where("isDeleted", "!=", true)));
            if (querySnapshot.empty) {
                return { success: true, data: [] };
            }
            const invitations = [];
            querySnapshot.forEach((doc) => {
                invitations.push({ id: doc.id, ...doc.data() });
            });
            return { success: true, data: invitations };
        } catch (error) {
            console.error("Error fetching invitations:", error);
            return { success: false, error: error.message };
        }
    }

    async deleteInvitation(id) {
        if (!id) {
            console.error("Invalid invitation id:", id);
            return { success: false, error: "Invalid invitation id" };
        }
        try {
            const docRef = doc(db, "invitations", id);
            await setDoc(docRef, { isDeleted: true, updatedAt: serverTimestamp(), deletedAt: serverTimestamp() }, { merge: true });
            return { success: true };
        } catch (error) {
            console.error("Error deleting invitation:", error);
            return { success: false, error: error.message };
        }
    }

    async hardDeleteInvitation(id) {
        if (!id) {
            console.error("Invalid invitation id:", id);
            return { success: false, error: "Invalid invitation id" };
        }
        try {
            const docRef = doc(db, "invitations", id);
            await deleteDoc(docRef);
            return { success: true };
        } catch (error) {
            console.error("Error hard deleting invitation:", error);
            return { success: false, error: error.message };
        }
    }

    async getPendingInvitationCount(userId) {
        if (!userId) {
            console.error("Invalid user ID:", userId);
            return { success: false, error: "Invalid user ID" };
        }
        try {
            const q = query(
                collection(db, "invitations"),
                where("invitedById", "==", userId),
                where("accepted", "==", false),
                where("isDeleted", "!=", true)
            );
            const querySnapshot = await getDocs(q);
            return { success: true, count: querySnapshot.size };
        } catch (error) {
            console.error("Error fetching pending invitation count:", error);
            return { success: false, error: error.message };
        }
    }

    async getConsumerCountByUserId(userId) {
        if (!userId) {
            console.error("Invalid user ID:", userId);
            return { success: false, error: "Invalid user ID" };
        }
        try {
            const q = query(
                collection(db, "invitations"),
                where("invitedById", "==", userId),
                where("accepted", "==", true),
                where("role", "==", "consumer")
            );
            const querySnapshot = await getDocs(q);
            return { success: true, count: querySnapshot.size };
        } catch (error) {
            console.error("Error fetching consumer count:", error);
            return { success: false, error: error.message };
        }
    }
}

export default InvitationApi;